/**
 * User-Facing Labels Configuration
 * Centralized location for all UI text used in navigation, headers, and user-facing content
 *
 * USAGE: Import specific constants where needed
 * import { NAVIGATION_LABELS, PAGE_TITLES } from '../constants/userFacingLabels';
 */

// =============================================================================
// APPLICATION BRANDING
// =============================================================================
export const APP_BRANDING = {
  name: 'SLAM Sports Intelligence',
  shortName: 'SLAM',
  tagline: 'AI-Powered Athletic Analytics',
  headerTitle: 'SLAM Sports Intelligence Portal',
  settingsTitle: 'Platform Settings',
};

// =============================================================================
// SPORT TERMINOLOGY
// =============================================================================
export const SPORT_BRANDING = {
  baseball: {
    analysisPlatform: 'THE WIRE',
    analysisPlatformFull: 'THE WIRE - Baseball Intelligence',
    tagline: 'Comprehensive pitching and hitting analysis',
  },
  basketball: {
    analysisPlatform: 'COURT VISION',
    analysisPlatformFull: 'COURT VISION - Basketball Analytics',
    tagline: 'Advanced shooting and movement analysis',
  },
};

// =============================================================================
// ROLE LABELS
// =============================================================================
export const ROLE_LABELS = {
  ADMIN: 'Administrator',
  ATHLETIC_DIRECTOR: 'Athletic Director',
  HEAD_COACH: 'Head Coach',
  ASSISTANT_COACH: 'Assistant Coach',
  ANALYST_SCOUT: 'Analyst/Scout',
  STAFF: 'Staff',
};

// =============================================================================
// NAVIGATION LABELS WITH DESCRIPTIONS
// Reorganized for coach-friendly workflow: daily tasks first, advanced tools at bottom
// =============================================================================
export const NAVIGATION_LABELS = {
  // MAIN Section - Daily Coach Workflow (prioritized order)
  main: {
    sectionLabel: 'MAIN',
    items: {
      gettingStarted: {
        label: 'Getting Started',
        description: 'Platform overview and quick start guide',
        tooltip: 'Learn how the platform works and view key metrics',
        oldLabel: 'How it works',
        route: '/dashboard',
      },
      myTeam: {
        label: 'Roster Management',
        description: 'Current roster overview and player cards',
        tooltip: 'View and manage your team roster',
        route: '/my-team',
        legacyRoute: '/my-team-player',
      },
      videoAnalytics: {
        label: 'Video Analytics',
        description: 'Game film analysis and breakdown tools',
        tooltip: 'Upload and analyze practice and game video',
        route: '/video-analytics',
      },
      myDashboard: {
        label: 'My Dashboard',
        description: 'Your personalized analytics overview',
        tooltip: 'View your customized dashboard with key insights',
        route: '/my-dashboard',
      },
      slamAI: {
        label: 'SLAM AI',
        description: 'Chat with our AI-powered recruiting assistant',
        tooltip: 'Ask questions about players, get scouting insights, and more',
        oldLabel: 'Ask Benny',
        route: '/slam-ai',
        legacyRoute: '/ask-benny',
      },
      customDashboards: {
        label: 'Custom Dashboards',
        description: 'Build and save custom analytics views',
        tooltip: 'Create personalized widgets and save custom layouts',
        oldLabel: 'Widget',
        route: '/custom-dashboards',
        legacyRoute: '/saved-pages',
      },
    },
  },

  // RECRUITING Section - Talent acquisition
  recruiting: {
    sectionLabel: 'RECRUITING',
    items: {
      transferPortal: {
        label: 'Transfer Portal',
        description: 'Browse and track transfer candidates',
        tooltip: 'Search the transfer portal with AI-powered matching',
        route: '/transfer-portal',
        legacyRoute: '/scouting',
      },
      rosterManagement: {
        label: 'Roster Management',
        description: 'Comprehensive roster database',
        tooltip: 'Browse and manage player roster with detailed profiles',
        route: '/roster',
        legacyRoute: '/player-roster',
      },
      preCollegeProspects: {
        label: 'Pre-College Prospects',
        description: 'High school and prep recruiting',
        tooltip: 'Track and evaluate pre-college prospects',
        oldLabel: 'Pre-College Players',
        route: '/pre-college',
        legacyRoute: '/pre-college-player',
      },
      nilValuation: {
        label: 'NIL Valuation',
        description: 'Name, Image, Likeness value estimation',
        tooltip: 'Calculate NIL value and market comparisons',
        route: '/nil-valuation',
        legacyRoute: '/valuation-engine',
      },
    },
  },

  // DEVELOPMENT Section - AI-powered analysis and player improvement
  development: {
    sectionLabel: 'DEVELOPMENT',
    items: {
      aiVideoAnalyzer: {
        label: 'AI Video Analyzer',
        description: 'Automated video analysis using AI',
        tooltip: 'Gemini-powered video analysis with expert personas',
        route: '/video-analyzer',
      },
      developmentPlans: {
        label: 'Development Plans',
        description: 'Player improvement tracking and projections',
        tooltip: 'Track player development and create improvement plans',
        route: '/development',
      },
      theWire: {
        label: 'THE WIRE',
        description: 'Baseball intelligence and batch analysis reports',
        tooltip: 'Generate comprehensive baseball scouting reports',
        route: '/the-wire-report',
      },
      biomechanics: {
        label: 'Biomechanics',
        description: 'Movement and mechanics analysis',
        tooltip: 'Analyze pitching/shooting mechanics and movement patterns',
        route: '/biomechanics',
      },
      performanceTrends: {
        label: 'Performance Trends',
        description: 'Historical performance tracking',
        tooltip: 'Coming soon - Track performance over time',
        route: null,
        isRoadmap: true,
      },
      injuryRisk: {
        label: 'Injury Risk',
        description: 'Injury prevention and risk assessment',
        tooltip: 'Coming soon - Monitor workload and injury risk factors',
        route: null,
        isRoadmap: true,
      },
    },
  },

  // Legacy: PLAYER DEVELOPMENT Section (for backwards compatibility)
  playerDevelopment: {
    sectionLabel: 'PLAYER DEVELOPMENT',
    items: {
      myTeam: {
        label: 'Roster Management',
        description: 'Current roster overview and player cards',
        tooltip: 'View and manage your team roster',
        route: '/my-team',
        legacyRoute: '/my-team-player',
      },
      videoAnalytics: {
        label: 'Video Analytics',
        description: 'Game film analysis and breakdown tools',
        tooltip: 'Upload and analyze practice and game video',
        route: '/video-analytics',
      },
      aiVideoAnalyzer: {
        label: 'AI Video Analyzer',
        description: 'Automated video analysis using AI',
        tooltip: 'Gemini-powered video analysis with expert personas',
        route: '/video-analyzer',
      },
      biomechanics: {
        label: 'Biomechanics',
        description: 'Movement and mechanics analysis',
        tooltip: 'Analyze pitching/shooting mechanics and movement patterns',
        route: '/biomechanics',
      },
      developmentPlans: {
        label: 'Development Plans',
        description: 'Player improvement tracking and projections',
        tooltip: 'Track player development and create improvement plans',
        route: '/development',
      },
      theWire: {
        label: 'THE WIRE',
        description: 'Baseball intelligence and batch analysis reports',
        tooltip: 'Generate comprehensive baseball scouting reports',
        route: '/the-wire-report',
      },
      performanceTrends: {
        label: 'Performance Trends',
        description: 'Historical performance tracking',
        tooltip: 'Coming soon - Track performance over time',
        route: null,
        isRoadmap: true,
      },
      injuryRisk: {
        label: 'Injury Risk',
        description: 'Injury prevention and risk assessment',
        tooltip: 'Coming soon - Monitor workload and injury risk factors',
        route: null,
        isRoadmap: true,
      },
    },
  },

  // Legacy: SCOUTING Section (merged into recruiting, kept for backwards compatibility)
  scouting: {
    sectionLabel: 'SCOUTING',
    items: {
      rosterManagement: {
        label: 'Roster Management',
        description: 'Comprehensive roster database',
        tooltip: 'Browse and manage player roster with detailed profiles',
        route: '/roster',
        legacyRoute: '/player-roster',
      },
      topMatches: {
        label: 'Top Matches',
        description: 'Best-fit player recommendations',
        tooltip: 'Coming soon - AI-powered player matching',
        route: null,
        isRoadmap: true,
      },
      decisionSupport: {
        label: 'Decision Support',
        description: 'Data-driven roster decisions',
        tooltip: 'Coming soon - Analytics for roster decisions',
        oldLabel: 'Decision Making',
        route: null,
        isRoadmap: true,
      },
      culturalFit: {
        label: 'Cultural Fit',
        description: 'Team chemistry assessment',
        tooltip: 'Coming soon - Evaluate player fit with team culture',
        route: null,
        isRoadmap: true,
      },
      externalDatabases: {
        label: 'External Databases',
        description: 'Third-party scouting data integration',
        tooltip: 'Coming soon - Access external scouting reports',
        oldLabel: 'Scouting Databases',
        route: null,
        isRoadmap: true,
      },
      playerValuation: {
        label: 'Player Valuation',
        description: 'Performance-based value assessment',
        tooltip: 'Coming soon - Advanced player valuation metrics',
        route: null,
        isRoadmap: true,
      },
    },
  },

  // SYSTEM Section - Admin, Configuration & Advanced AI Tools
  // AI Analysis Hub moved here (was previously "How it Works" / Agent Universe)
  system: {
    sectionLabel: 'SYSTEM',
    items: {
      settings: {
        label: 'Settings',
        description: 'Platform and team configuration',
        tooltip: 'Configure sport, team, and display preferences',
        route: '/settings',
      },
      reports: {
        label: 'Reports',
        description: 'Generate and export reports',
        tooltip: 'Create PDF reports and export data',
        route: '/reports',
      },
      integrations: {
        label: 'Integrations',
        description: 'Connect external data sources',
        tooltip: 'Manage connections to Trackman, Rapsodo, Synergy, etc.',
        route: '/integrations',
      },
      userManagement: {
        label: 'User Management',
        description: 'Manage users and access controls',
        tooltip: 'Admin only - Configure user roles and permissions',
        oldLabel: 'Permissions',
        route: '/user-management',
        legacyRoute: '/permissions',
        adminOnly: true,
      },
      aiAnalysisHub: {
        label: 'AI Analysis Hub',
        description: 'Multi-agent AI orchestration for deep analysis',
        tooltip: 'Advanced AI agents for comprehensive player analysis',
        oldLabel: 'Agent Universe',
        route: '/ai-analysis-hub',
        legacyRoute: '/agent-universe',
      },
    },
  },
};

// =============================================================================
// ROLE-SPECIFIC NAVIGATION PRESETS
// =============================================================================
export const ROLE_NAVIGATION_PRESETS = {
  ATHLETIC_DIRECTOR: {
    displayName: 'Athletic Director',
    primarySections: ['main', 'system'],
    visibleItems: [
      'gettingStarted', 'myDashboard',
      'nilValuation',
      'reports', 'settings',
    ],
    hiddenItems: ['biomechanics', 'videoAnalytics', 'aiVideoAnalyzer'],
    customLabels: {
      reports: 'Executive Reports',
      myDashboard: 'Program Overview',
    },
  },

  HEAD_COACH: {
    displayName: 'Head Coach',
    primarySections: ['playerDevelopment', 'recruiting'],
    visibleItems: [
      'aiAssistant', 'myDashboard',
      'myTeam', 'videoAnalytics', 'developmentPlans', 'biomechanics',
      'rosterManagement',
      'transferPortal',
      'reports',
    ],
    customLabels: {
      aiAssistant: 'Analysis Chat',
      rosterManagement: 'Roster Overview',
    },
  },

  ASSISTANT_COACH: {
    displayName: 'Assistant Coach',
    primarySections: ['playerDevelopment'],
    visibleItems: [
      'aiAssistant', 'myDashboard',
      'myTeam', 'videoAnalytics', 'biomechanics', 'developmentPlans',
      'rosterManagement',
      'reports',
    ],
    customLabels: {},
  },

  ANALYST_SCOUT: {
    displayName: 'Analyst/Scout',
    primarySections: ['playerDevelopment', 'scouting'],
    visibleItems: [
      'aiAssistant', 'customDashboards',
      'videoAnalytics', 'aiVideoAnalyzer', 'biomechanics',
      'rosterManagement',
      'transferPortal', 'aiAnalysisHub',
      'reports',
    ],
    customLabels: {
      aiAssistant: 'Query Interface',
      aiAnalysisHub: 'Multi-Agent Analysis',
    },
  },

  STAFF: {
    displayName: 'Support Staff',
    primarySections: ['main'],
    visibleItems: [
      'gettingStarted', 'myDashboard',
      'settings',
    ],
    hiddenSections: ['playerDevelopment', 'scouting', 'recruiting'],
    customLabels: {},
  },

  ADMIN: {
    displayName: 'Administrator',
    primarySections: ['main', 'system'],
    visibleItems: 'all',
    customLabels: {},
  },
};

// =============================================================================
// PAGE TITLES (for browser tab and page headers)
// =============================================================================
export const PAGE_TITLES = {
  '/dashboard': 'Getting Started | SLAM Sports',
  '/my-dashboard': 'My Dashboard | SLAM Sports',
  '/ask-benny': 'SLAM AI | SLAM Sports',
  '/ai-assistant': 'SLAM AI | SLAM Sports',
  '/saved-pages': 'Custom Dashboards | SLAM Sports',
  '/custom-dashboards': 'Custom Dashboards | SLAM Sports',
  '/my-team-player': 'My Team | SLAM Sports',
  '/my-team': 'My Team | SLAM Sports',
  '/video-analytics': 'Video Analytics | SLAM Sports',
  '/video-analyzer': 'AI Video Analyzer | SLAM Sports',
  '/biomechanics': 'Biomechanics | SLAM Sports',
  '/development': 'Development Plans | SLAM Sports',
  '/player-roster': 'Roster Management | SLAM Sports',
  '/roster': 'Roster Management | SLAM Sports',
  '/scouting': 'Transfer Portal | SLAM Sports',
  '/transfer-portal': 'Transfer Portal | SLAM Sports',
  '/pre-college-player': 'Pre-College Prospects | SLAM Sports',
  '/pre-college': 'Pre-College Prospects | SLAM Sports',
  '/valuation-engine': 'NIL Valuation | SLAM Sports',
  '/nil-valuation': 'NIL Valuation | SLAM Sports',
  '/agent-universe': 'AI Analysis Hub | SLAM Sports',
  '/ai-analysis-hub': 'AI Analysis Hub | SLAM Sports',
  '/the-wire-report': 'THE WIRE | SLAM Sports',
  '/reports': 'Reports | SLAM Sports',
  '/settings': 'Settings | SLAM Sports',
  '/integrations': 'Integrations | SLAM Sports',
  '/permissions': 'User Management | SLAM Sports',
  '/user-management': 'User Management | SLAM Sports',
  '/login': 'Sign In | SLAM Sports',
};

// =============================================================================
// SECTION HEADERS (for page content areas)
// =============================================================================
export const SECTION_HEADERS = {
  dashboard: {
    main: 'Platform Overview',
    dataIntegration: 'Data Integration Hub',
    quickStats: 'Key Metrics',
  },
  settings: {
    main: 'Platform Settings',
    sportTeam: 'Sport & Team Configuration',
    athleticDepartment: 'Performance Criteria',
    rolePreferences: 'Role Preferences',
  },
  videoAnalytics: {
    main: 'Video Analysis Center',
    upload: 'Upload Media',
    analysis: 'Analysis Results',
  },
  scouting: {
    main: 'Transfer Portal Database',
    filters: 'Search Filters',
    results: 'Candidates',
  },
  permissions: {
    main: 'User Management',
    users: 'User Accounts',
    roles: 'Role Templates',
    pending: 'Pending Approvals',
  },
};

// =============================================================================
// SETTINGS LABELS
// =============================================================================
export const SETTINGS_LABELS = {
  pageTitle: 'Platform Settings',
  pageDescription: 'Configure your sport, team settings, and performance criteria',
  tabs: {
    sportTeam: 'Sport & Team',
    athleticDepartment: 'Performance Criteria',
    rolePreferences: 'Role Preferences',
    notifications: 'Notifications',
    appearance: 'Appearance',
  },
  sections: {
    sportSelection: 'Sport Selection',
    teamConfiguration: 'Team Configuration',
    performanceCriteria: 'Performance Criteria',
    weightedScoring: 'Weighted Scoring',
  },
  placeholders: {
    selectSport: 'Select your sport',
    selectTeam: 'Select your team',
  },
  help: {
    sportSelection: 'Choose your primary sport for analysis',
    teamConfiguration: 'Configure team-specific settings',
    performanceCriteria: 'Set weights for player evaluation metrics',
  },
};

// =============================================================================
// BUTTON LABELS
// =============================================================================
export const BUTTON_LABELS = {
  actions: {
    save: 'Save Changes',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create New',
    export: 'Export',
    import: 'Import',
    upload: 'Upload',
    analyze: 'Run Analysis',
    generate: 'Generate Report',
    refresh: 'Refresh Data',
  },
  navigation: {
    back: 'Go Back',
    next: 'Continue',
    finish: 'Complete',
    viewAll: 'View All',
    seeMore: 'See More',
    expand: 'Expand',
    collapse: 'Collapse',
  },
  auth: {
    login: 'Sign In',
    logout: 'Sign Out',
    register: 'Create Account',
  },
};

// =============================================================================
// FEATURE DESCRIPTIONS (for tooltips and help text)
// =============================================================================
export const FEATURE_DESCRIPTIONS = {
  aiAssistant: 'Our AI assistant uses advanced language models to help you find players, analyze statistics, and get insights. Ask questions in natural language about any aspect of your team or recruiting targets.',

  aiAnalysisHub: 'The AI Analysis Hub orchestrates multiple specialized AI agents working together to provide comprehensive player analysis. Each agent specializes in different aspects like biomechanics, statistics, or scouting.',

  transferPortal: 'Search and filter through the transfer portal database. Use AI-powered matching to find players that fit your team needs, budget, and playing style.',

  videoAnalyzer: 'Upload video clips and let our AI analyze pitching mechanics, shooting form, or any athletic movement. Powered by Google Gemini with sport-specific expert personas.',

  customDashboards: 'Build personalized analytics dashboards by combining different widgets. Save your layouts and access them quickly from the sidebar.',

  nilValuation: 'Estimate the NIL (Name, Image, Likeness) value of players based on social media presence, performance metrics, market size, and comparable deals.',
};

// =============================================================================
// STATUS MESSAGES
// =============================================================================
export const STATUS_MESSAGES = {
  loading: {
    generic: 'Loading...',
    players: 'Loading players...',
    analysis: 'Running analysis...',
    video: 'Processing video...',
  },
  success: {
    saved: 'Changes saved successfully',
    uploaded: 'File uploaded successfully',
    deleted: 'Item deleted',
    exported: 'Export complete',
  },
  error: {
    generic: 'Something went wrong. Please try again.',
    network: 'Unable to connect to server. Check your connection.',
    auth: 'Session expired. Please sign in again.',
    permission: 'You do not have permission for this action.',
  },
  empty: {
    noResults: 'No results found',
    noPlayers: 'No players match your criteria',
    noVideos: 'No videos uploaded yet',
  },
};

// =============================================================================
// ROUTE MAPPINGS (for standardization and redirects)
// =============================================================================
export const ROUTE_MAPPINGS = {
  // Standardized routes (new)
  standard: {
    dashboard: '/dashboard',
    myDashboard: '/my-dashboard',
    slamAI: '/slam-ai',
    customDashboards: '/custom-dashboards',
    myTeam: '/my-team',
    videoAnalytics: '/video-analytics',
    videoAnalyzer: '/video-analyzer',
    biomechanics: '/biomechanics',
    development: '/development',
    roster: '/roster',
    transferPortal: '/transfer-portal',
    preCollege: '/pre-college',
    nilValuation: '/nil-valuation',
    aiAnalysisHub: '/ai-analysis-hub',
    reports: '/reports',
    settings: '/settings',
    integrations: '/integrations',
    userManagement: '/user-management',
  },

  // Legacy route redirects (old -> new)
  legacy: {
    '/ask-benny': '/slam-ai',
    '/ai-assistant': '/slam-ai',
    '/saved-pages': '/custom-dashboards',
    '/player-roster': '/roster',
    '/Roster': '/roster',
    '/scouting': '/transfer-portal',
    '/pre-college-player': '/pre-college',
    '/valuation-engine': '/nil-valuation',
    '/agent-universe': '/ai-analysis-hub',
    '/permissions': '/user-management',
    '/my-team-player': '/my-team',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get navigation label for a route
 * @param {string} route - Route path
 * @returns {string} Label for the route
 */
export function getLabelForRoute(route) {
  for (const section of Object.values(NAVIGATION_LABELS)) {
    for (const item of Object.values(section.items || {})) {
      if (item.route === route || item.legacyRoute === route) {
        return item.label;
      }
    }
  }
  return route;
}

/**
 * Get navigation items visible for a role
 * @param {string} roleId - Role ID
 * @returns {Array} Array of visible navigation items
 */
export function getVisibleItemsForRole(roleId) {
  const preset = ROLE_NAVIGATION_PRESETS[roleId];
  if (!preset) return [];

  if (preset.visibleItems === 'all') {
    // Return all non-roadmap items
    const items = [];
    for (const section of Object.values(NAVIGATION_LABELS)) {
      for (const [key, item] of Object.entries(section.items || {})) {
        if (!item.isRoadmap && item.route) {
          items.push({ key, ...item });
        }
      }
    }
    return items;
  }

  return preset.visibleItems || [];
}

/**
 * Check if a route is accessible by a role
 * @param {string} route - Route path
 * @param {string} roleId - Role ID
 * @returns {boolean} Whether the route is accessible
 */
export function isRouteAccessibleByRole(route, roleId) {
  const preset = ROLE_NAVIGATION_PRESETS[roleId];
  if (!preset) return false;

  if (preset.visibleItems === 'all') return true;

  // Find the item for this route
  for (const section of Object.values(NAVIGATION_LABELS)) {
    for (const [key, item] of Object.entries(section.items || {})) {
      if (item.route === route || item.legacyRoute === route) {
        return preset.visibleItems.includes(key);
      }
    }
  }

  return false;
}

// =============================================================================
// BACKWARDS-COMPATIBLE FLAT NAVIGATION LABELS
// Used by Sidebar.js - maps NAVIGATION_LABELS.ITEM_NAME to nested structure
// Updated to reflect new coach-friendly navigation order
// =============================================================================
// MAIN section items (now includes My Team and Video Analytics for coach workflow)
NAVIGATION_LABELS.GETTING_STARTED = NAVIGATION_LABELS.main.items.gettingStarted;
NAVIGATION_LABELS.MY_TEAM = NAVIGATION_LABELS.main.items.myTeam;
NAVIGATION_LABELS.VIDEO_ANALYTICS = NAVIGATION_LABELS.main.items.videoAnalytics;
NAVIGATION_LABELS.VIDEO_ANALYTICS_V2 = { label: 'Film Room', description: 'Cut / Reel / Vault — biomechanics, session film, bulk archive', route: '/video-analytics-v2' };
NAVIGATION_LABELS.MY_DASHBOARD = NAVIGATION_LABELS.main.items.myDashboard;
NAVIGATION_LABELS.AI_ASSISTANT = NAVIGATION_LABELS.main.items.aiAssistant;
NAVIGATION_LABELS.CUSTOM_DASHBOARDS = NAVIGATION_LABELS.main.items.customDashboards;
// RECRUITING section items
NAVIGATION_LABELS.TRANSFER_PORTAL = NAVIGATION_LABELS.recruiting.items.transferPortal;
NAVIGATION_LABELS.ROSTER_MANAGEMENT = NAVIGATION_LABELS.recruiting.items.rosterManagement;
NAVIGATION_LABELS.PRE_COLLEGE = NAVIGATION_LABELS.recruiting.items.preCollegeProspects;
NAVIGATION_LABELS.NIL_VALUATION = NAVIGATION_LABELS.recruiting.items.nilValuation;
// DEVELOPMENT section items
NAVIGATION_LABELS.AI_VIDEO_ANALYZER = NAVIGATION_LABELS.development.items.aiVideoAnalyzer;
NAVIGATION_LABELS.DEVELOPMENT = NAVIGATION_LABELS.development.items.developmentPlans;
NAVIGATION_LABELS.THE_WIRE = NAVIGATION_LABELS.development.items.theWire;
NAVIGATION_LABELS.BIOMECHANICS = NAVIGATION_LABELS.development.items.biomechanics;
// SYSTEM section items (AI Analysis Hub moved here from recruiting)
NAVIGATION_LABELS.SETTINGS = NAVIGATION_LABELS.system.items.settings;
NAVIGATION_LABELS.REPORTS = NAVIGATION_LABELS.system.items.reports;
NAVIGATION_LABELS.INTEGRATIONS = NAVIGATION_LABELS.system.items.integrations;
NAVIGATION_LABELS.USER_MANAGEMENT = NAVIGATION_LABELS.system.items.userManagement;
NAVIGATION_LABELS.AI_ANALYSIS_HUB = NAVIGATION_LABELS.system.items.aiAnalysisHub;

// =============================================================================
// BACKWARDS-COMPATIBLE FLAT ROUTE MAPPINGS
// Used by Sidebar.js - maps ROUTE_MAPPINGS.ITEM_NAME.current to routes
// =============================================================================
ROUTE_MAPPINGS.GETTING_STARTED = { current: '/dashboard', legacy: null };
ROUTE_MAPPINGS.MY_DASHBOARD = { current: '/my-dashboard', legacy: null };
ROUTE_MAPPINGS.SLAM_AI = { current: '/slam-ai', legacy: '/ask-benny' };
ROUTE_MAPPINGS.CUSTOM_DASHBOARDS = { current: '/custom-dashboards', legacy: '/saved-pages' };
ROUTE_MAPPINGS.MY_TEAM = { current: '/my-team', legacy: '/my-team-player' };
ROUTE_MAPPINGS.VIDEO_ANALYTICS = { current: '/video-analytics', legacy: null };
ROUTE_MAPPINGS.VIDEO_ANALYTICS_V2 = { current: '/video-analytics-v2', legacy: null };
ROUTE_MAPPINGS.AI_VIDEO_ANALYZER = { current: '/video-analyzer', legacy: null };
ROUTE_MAPPINGS.BIOMECHANICS = { current: '/biomechanics', legacy: null };
ROUTE_MAPPINGS.DEVELOPMENT = { current: '/development', legacy: null };
ROUTE_MAPPINGS.THE_WIRE = { current: '/the-wire-report', legacy: null };
ROUTE_MAPPINGS.ROSTER_MANAGEMENT = { current: '/roster', legacy: '/player-roster' };
ROUTE_MAPPINGS.TRANSFER_PORTAL = { current: '/transfer-portal', legacy: '/scouting' };
ROUTE_MAPPINGS.PRE_COLLEGE = { current: '/pre-college', legacy: '/pre-college-player' };
ROUTE_MAPPINGS.NIL_VALUATION = { current: '/nil-valuation', legacy: '/valuation-engine' };
ROUTE_MAPPINGS.AI_ANALYSIS_HUB = { current: '/ai-analysis-hub', legacy: '/agent-universe' };
ROUTE_MAPPINGS.REPORTS = { current: '/reports', legacy: null };
ROUTE_MAPPINGS.SETTINGS = { current: '/settings', legacy: null };
ROUTE_MAPPINGS.INTEGRATIONS = { current: '/integrations', legacy: null };
ROUTE_MAPPINGS.USER_MANAGEMENT = { current: '/user-management', legacy: '/permissions' };

// Default export for convenience
export default {
  APP_BRANDING,
  SPORT_BRANDING,
  ROLE_LABELS,
  NAVIGATION_LABELS,
  ROLE_NAVIGATION_PRESETS,
  PAGE_TITLES,
  SECTION_HEADERS,
  SETTINGS_LABELS,
  BUTTON_LABELS,
  FEATURE_DESCRIPTIONS,
  STATUS_MESSAGES,
  ROUTE_MAPPINGS,
  getLabelForRoute,
  getVisibleItemsForRole,
  isRouteAccessibleByRole,
};
